export const INBOX_PANE_GAP_PX = 8;
/** Desktop shell padding on each side of the Inbox container (`p-2`). */
export const INBOX_SHELL_PADDING_PX = 8;
export const INBOX_LIST_WIDTH_PX = 320;
export const INBOX_LIST_NARROW_WIDTH_PX = 288;
export const INBOX_LIST_COLLAPSED_WIDTH_PX = 64;
/** Narrowest thread that still fits the header, two-thirds-width bubbles, and the composer row. */
export const INBOX_THREAD_MIN_WIDTH_PX = 520;
export const INBOX_DETAILS_DOCKED_WIDTH_PX = 300;
export const INBOX_DETAILS_DRAWER_WIDTH_PX = 320;

/** Below this Inbox container width the list column narrows to 288px. */
export const INBOX_LIST_NARROW_BELOW_PX = 1000;

/** Shell padding, list, thread minimum, and docked Details must all fit inside the Inbox container. */
export const INBOX_DETAILS_DOCK_MIN_WIDTH_PX =
  INBOX_SHELL_PADDING_PX * 2 +
  INBOX_LIST_WIDTH_PX +
  INBOX_PANE_GAP_PX +
  INBOX_THREAD_MIN_WIDTH_PX +
  INBOX_PANE_GAP_PX +
  INBOX_DETAILS_DOCKED_WIDTH_PX;

/** Width buckets of the measured Inbox container (never the viewport). */
export type InboxContainerSize = "narrow" | "medium" | "wide";

export function inboxContainerSize(containerWidthPx: number): InboxContainerSize {
  if (containerWidthPx >= INBOX_DETAILS_DOCK_MIN_WIDTH_PX) return "wide";
  if (containerWidthPx >= INBOX_LIST_NARROW_BELOW_PX) return "medium";
  return "narrow";
}

/**
 * mobile: one full-width pane at a time.
 * drawer: Details opens over the thread from inline-end.
 * docked: Details sits beside the thread as a third column.
 */
export type InboxLayoutMode = "mobile" | "drawer" | "docked";

export function inboxLayoutMode(input: {
  containerSize: InboxContainerSize;
  mobileViewport: boolean;
}): InboxLayoutMode {
  if (input.mobileViewport) return "mobile";
  return input.containerSize === "wide" ? "docked" : "drawer";
}

/** Width the full list keeps while collapsing, so rows do not reflow mid-animation. */
export function inboxListExpandedWidthPx(containerSize: InboxContainerSize): number {
  return containerSize === "narrow" ? INBOX_LIST_NARROW_WIDTH_PX : INBOX_LIST_WIDTH_PX;
}

export function inboxListWidthPx(input: {
  containerSize: InboxContainerSize;
  collapsed: boolean;
}): number {
  return input.collapsed
    ? INBOX_LIST_COLLAPSED_WIDTH_PX
    : inboxListExpandedWidthPx(input.containerSize);
}

/**
 * Exactly one Details toggle is visible. While Details is shown on desktop the collapse
 * control lives in its header; otherwise the thread header offers the opener.
 */
export function showThreadDetailsOpener(mode: InboxLayoutMode, detailsOpen: boolean): boolean {
  switch (mode) {
    case "mobile":
      return true;
    case "drawer":
    case "docked":
      return !detailsOpen;
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}
